import { z } from 'zod';

/**
 * Shared configuration schemas and validators
 * Used by client, worker, and scripts packages
 */

// ============================================================================
// Custom Validators
// ============================================================================

/**
 * Base64url string validator
 * Matches the pattern used throughout the codebase
 */
export const base64url = () =>
  z.string().regex(/^[A-Za-z0-9_-]+$/, 'Must be valid base64url');

/**
 * Base64url with max length
 */
export const base64urlWithMax = (max: number) =>
  z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/, 'Must be valid base64url')
    .max(max, `Must be at most ${max} characters`);

/**
 * Boolean from string (handles common truthy values)
 * Used for env vars that come as strings
 */
export const booleanFromString = () =>
  z
    .string()
    .transform((val) => {
      const normalized = val.toLowerCase().trim();
      return ['1', 'true', 'yes', 'on'].includes(normalized);
    })
    .pipe(z.boolean());

/**
 * Number from string with validation
 */
export const numberFromString = (options?: {
  min?: number;
  max?: number;
  finite?: boolean;
}) =>
  z
    .string()
    .transform((val) => Number(val))
    .pipe(
      z
        .number()
        .refine(
          (n) => (options?.finite !== false ? Number.isFinite(n) : true),
          'Must be a finite number',
        )
        .refine((n) => (options?.min !== undefined ? n >= options.min : true), {
          message: `Must be at least ${options?.min}`,
        })
        .refine((n) => (options?.max !== undefined ? n <= options.max : true), {
          message: `Must be at most ${options?.max}`,
        }),
    );

/**
 * Non-empty string validator
 */
export const nonEmptyString = (fieldName: string) =>
  z.string().min(1, `${fieldName} is required and cannot be empty`);

/**
 * URL validator
 */
export const urlString = (fieldName: string) =>
  z.string().url(`${fieldName} must be a valid URL`);

/**
 * Optional URL (can be empty string or valid URL)
 */
export const optionalUrlString = () =>
  z
    .string()
    .optional()
    .transform((val) => (val && val.length > 0 ? val : undefined))
    .pipe(z.string().url().optional());

// ============================================================================
// VOPRF Schemas
// ============================================================================

/**
 * VOPRF key record (single key)
 */
export const voprfKeyRecordSchema = z.object({
  sk: base64url(),
  pk: base64url(),
  suite: z.literal('VOPRF-P256-SHA256'),
});

export type VoprfKeyRecord = z.infer<typeof voprfKeyRecordSchema>;

/**
 * VOPRF keys object (map of key ID to key record)
 */
export const voprfKeysSchema = z
  .record(z.string(), voprfKeyRecordSchema)
  .refine((keys) => Object.keys(keys).length > 0, 'At least one VOPRF key is required');

export type VoprfKeys = z.infer<typeof voprfKeysSchema>;

/**
 * VOPRF public keys (map of key ID to public key base64url)
 */
export const voprfPublicKeysSchema = z
  .record(z.string(), base64url())
  .refine(
    (keys) => Object.keys(keys).length > 0,
    'At least one VOPRF public key is required',
  );

export type VoprfPublicKeys = z.infer<typeof voprfPublicKeysSchema>;

// ============================================================================
// Argon2 Schemas
// ============================================================================

/**
 * Argon2 profile (used for both autotuning and fixed profiles)
 */
export const argon2ProfileSchema = z.object({
  memoryMiB: z.number().int().min(16).max(2048),
  timeCost: z.number().int().min(1).max(20),
  parallelism: z.number().int().min(1).max(16),
});

export type Argon2Profile = z.infer<typeof argon2ProfileSchema>;

/**
 * Argon2 KDF parameters (from stage payload)
 */
export const argon2KdfParamsSchema = z.object({
  hint: z.string(),
  minMemoryMiB: z.number().int().min(16),
  memoryMiB: z.number().int().min(16).optional(),
  timeCost: z.number().int().min(1).optional(),
  parallelism: z.number().int().min(1).optional(),
});

export type Argon2KdfParams = z.infer<typeof argon2KdfParamsSchema>;

// ============================================================================
// Rate Limiting Schemas
// ============================================================================

/**
 * Token bucket configuration
 */
export const tokenBucketConfigSchema = z.object({
  capacity: z.number().int().min(1).max(10000).default(200),
  refillRate: z
    .number()
    .min(0)
    .max(100)
    .default(1 / 300), // tokens per second
});

export type TokenBucketConfig = z.infer<typeof tokenBucketConfigSchema>;

// ============================================================================
// CORS Schemas
// ============================================================================

/**
 * CORS origins (comma-separated string or array)
 */
export const corsOriginsSchema = z
  .string()
  .transform((val) => {
    if (val === '*') return ['*'];
    return val
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean);
  })
  .pipe(z.array(z.string().min(1)));

export type CorsOrigins = z.infer<typeof corsOriginsSchema>;

// ============================================================================
// Development Mode Schemas
// ============================================================================

/**
 * Development mode configuration
 */
export const devModeConfigSchema = z.object({
  enabled: z.boolean(),
  turnstileBypassToken: z.string().default('ers-dev-turnstile-token'),
});

export type DevModeConfig = z.infer<typeof devModeConfigSchema>;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Parse JSON from string with better error messages
 */
export function parseJsonString<T>(
  jsonString: string,
  schema: z.ZodType<T>,
  fieldName: string,
): T {
  try {
    const parsed = JSON.parse(jsonString);
    return schema.parse(parsed);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const issues = error.issues
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join(', ');
      throw new Error(`Invalid ${fieldName}: ${issues}`);
    }
    if (error instanceof SyntaxError) {
      throw new Error(`Invalid JSON in ${fieldName}: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Validate and parse VOPRF keys from JSON string
 */
export function parseVoprfKeys(jsonString: string): VoprfKeys {
  return parseJsonString(jsonString, voprfKeysSchema, 'VOPRF_KEYS');
}

/**
 * Validate and parse a single VOPRF key from JSON string
 */
export function parseVoprfKey(jsonString: string): VoprfKeyRecord {
  return parseJsonString(jsonString, voprfKeyRecordSchema, 'VOPRF_KEY');
}

/**
 * Validate and parse VOPRF public keys from JSON string
 */
export function parseVoprfPublicKeys(jsonString: string): VoprfPublicKeys {
  return parseJsonString(jsonString, voprfPublicKeysSchema, 'VITE_OPRF_PUBKEYS');
}
